import { apiClient } from './client';

export interface FeedbackRule {
  id: string;
  rule: string;
  category: string;
  priority: number;
  isActive: boolean;
  status?: 'pending' | 'active' | 'rejected';
  sourceContentItemId?: string | null;
  sourceVersionNum?: number | null;
  diffSummary?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PendingFeedbackRule {
  id: string;
  rule: string;
  category: string;
  priority: number;
  diffSummary: string | null;
  sourceContentItemId: string | null;
  sourceVersionNum: number | null;
  sourceItemTitle: string | null;
  createdAt: string;
}

export async function getPendingFeedbackRules(): Promise<{ rules: PendingFeedbackRule[] }> {
  return apiClient.get('/content/feedback-rules/pending');
}

export async function getFeedbackRules(filters?: { category?: string; active?: boolean }): Promise<{ rules: FeedbackRule[] }> {
  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  if (filters?.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  return apiClient.get(`/content/feedback-rules${qs ? `?${qs}` : ''}`);
}

export async function createFeedbackRule(input: { rule: string; category?: string; priority?: number }): Promise<FeedbackRule> {
  return apiClient.post('/content/feedback-rules', input);
}

export async function patchFeedbackRule(
  id: string,
  updates: {
    rule?: string;
    category?: string;
    priority?: number;
    isActive?: boolean;
    status?: 'pending' | 'active' | 'rejected';
  },
): Promise<FeedbackRule> {
  return apiClient.patch(`/content/feedback-rules/${id}`, updates);
}

export async function deleteFeedbackRule(id: string): Promise<{ id: string; deleted: boolean }> {
  return apiClient.delete(`/content/feedback-rules/${id}`);
}

export interface RuleLibraryCandidate {
  id: string;
  title: string;
  type: string;
  status: 'draft' | 'review';
  replacements: number;
}

export type RuleLibraryPreview =
  | {
      supported: true;
      description: string;
      candidates: RuleLibraryCandidate[];
      totalReplacements: number;
    }
  | {
      supported: false;
      reason: string;
    };

export async function previewRuleAgainstLibrary(ruleId: string): Promise<RuleLibraryPreview> {
  return apiClient.get(`/content/feedback-rules/${ruleId}/library-preview`);
}

export interface RuleApplicationResult {
  ruleId: string;
  description: string;
  touched: Array<{ id: string; title: string; replacements: number; versionNum: number }>;
  skipped: Array<{ id: string; title: string; reason: string }>;
  failed: Array<{ id: string; title: string; reason: string }>;
  totalScanned: number;
}

export async function applyRuleToLibrary(
  ruleId: string,
  excludeContentItemIds: string[],
): Promise<RuleApplicationResult> {
  return apiClient.post(`/content/feedback-rules/${ruleId}/apply-to-library`, {
    excludeContentItemIds,
  });
}
